import {
  type AdminAssessmentDetail,
  type AdminAssessmentPage,
  type AdminAssessmentRiskLevel,
  type AdminAssessmentStatus,
  ASSESSMENT_RISK_LEVELS,
  ASSESSMENT_STATUSES,
} from '@nestjs-boilerplate/types';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@/lib/api-client';
import { manyOf, oneOf, toCsv } from '@/lib/query-params';
import { getAuthState } from '@/stores/auth.store';

export type {
  AdminAssessmentDetail,
  AdminAssessmentListItem,
  AdminAssessmentPage,
  AdminAssessmentReportState,
  AdminAssessmentRiskLevel,
  AdminAssessmentStatus,
} from '@nestjs-boilerplate/types';

/**
 * Filters the assessments screen can apply. A field left undefined is not
 * sent, so the server sees no filter at all rather than an empty one.
 */
export interface AdminAssessmentFilters {
  /** Any of these levels matches. Empty or absent means no risk filter. */
  riskLevel?: AdminAssessmentRiskLevel[];
  /** Any of these statuses matches. Absent leaves the API excluding DELETED. */
  status?: AdminAssessmentStatus[];
  owner?: string;
  search?: string;
  /** Set by the risk roll-up linking a firm through to its own assessments. */
  company?: string;
  from?: string;
  to?: string;
  sortBy?: 'createdAt' | 'riskLevel' | 'status';
  sortOrder?: 'asc' | 'desc';
}

/** The columns the API can sort by. */
const ASSESSMENT_SORT_COLUMNS: readonly NonNullable<AdminAssessmentFilters['sortBy']>[] = [
  'createdAt',
  'riskLevel',
  'status',
];

/** The sort directions the API accepts. */
const SORT_ORDERS: readonly NonNullable<AdminAssessmentFilters['sortOrder']>[] = ['asc', 'desc'];

/** The filters the API accepts as plain values, passed through as given. */
const VALUE_FILTERS = ['owner', 'search', 'company', 'from', 'to'] as const;

/** The filters the API accepts as a list of values, sent comma-separated. */
const LIST_FILTERS = [
  { key: 'riskLevel', allowed: ASSESSMENT_RISK_LEVELS },
  { key: 'status', allowed: ASSESSMENT_STATUSES },
] as const;

export const assessmentQueryKeys = {
  all: ['admin', 'assessments'] as const,
  list: (filters: AdminAssessmentFilters) => [...assessmentQueryKeys.all, filters] as const,
  detail: (id: string) => [...assessmentQueryKeys.all, 'detail', id] as const,
};

/**
 * Fetches one page of assessments.
 *
 * Parameters are read by name rather than iterated over, so only what the API
 * accepts is ever sent and the ordering pair can be narrowed on the way past.
 *
 * @param filters - Active filters
 * @param page - 1-based page number
 * @param pageSize - Rows per page
 */
export async function getAdminAssessments(
  filters: AdminAssessmentFilters,
  page = 1,
  pageSize = 20,
): Promise<AdminAssessmentPage> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

  for (const key of VALUE_FILTERS) {
    const value = filters[key];
    // Empty strings come from cleared inputs and must not become filters —
    // `owner=` would otherwise be sent and rejected as too short, turning an
    // emptied box into an error.
    if (value) {
      params.set(key, value);
    }
  }

  for (const { key, allowed } of LIST_FILTERS) {
    // An empty selection is "no filter" and must leave the parameter out
    // entirely: `status=` would be sent and rejected, turning a cleared
    // multi-select into an error card.
    const value = toCsv(manyOf(allowed, filters[key]));
    if (value) {
      params.set(key, value);
    }
  }

  // Only sent together: an order with no column would be a direction applied to
  // the server's default, which is not a thing the server offers.
  const sortBy = oneOf(ASSESSMENT_SORT_COLUMNS, filters.sortBy);
  if (sortBy) {
    params.set('sortBy', sortBy);
    params.set('sortOrder', oneOf(SORT_ORDERS, filters.sortOrder) ?? 'desc');
  }

  return apiClient.fetch<AdminAssessmentPage>(`/admin/assessments?${params.toString()}`);
}

/**
 * Fetches one assessment's summary.
 *
 * @param id - Assessment id
 */
export async function getAdminAssessment(id: string): Promise<AdminAssessmentDetail> {
  return apiClient.fetch<AdminAssessmentDetail>(`/admin/assessments/${id}`);
}

/**
 * Downloads one assessment's report as a PDF.
 *
 * Fetched rather than pointed at by an `<a href>`: the endpoint is
 * bearer-authenticated and a plain navigation would not carry the header. The
 * filename comes from the server's Content-Disposition, so an administrator's
 * copy is named the same as the owner's.
 *
 * Not routed through apiClient, which parses every response as JSON. Mirrors
 * the user-facing downloads in the risk-assessment feature.
 *
 * @param id - Assessment id
 * @throws Error carrying the server's message, so the screen can say whether
 *   the window has lapsed rather than only that something failed
 */
export async function downloadAdminReportPdf(id: string): Promise<void> {
  const { accessToken } = getAuthState();

  const response = await fetch(`/api/admin/assessments/${id}/report/pdf`, {
    credentials: 'include',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });

  if (!response.ok) {
    const message = await response
      .json()
      .then((body: { message?: string }) => body.message)
      .catch(() => null);
    throw new Error(message || 'Could not download this report');
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;

  const disposition = response.headers.get('Content-Disposition');
  anchor.download = disposition?.match(/filename="(.+)"/)?.[1] ?? `report-${id}.pdf`;

  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * Query hook for a page of assessments.
 *
 * Page-numbered rather than an infinite list, matching the admin user list and
 * the Pagination control the dashboard already uses. `placeholderData` keeps
 * the previous page on screen while the next loads, so paging does not blink
 * through an empty table.
 */
export function useAdminAssessments(
  filters: AdminAssessmentFilters,
  page: number,
  pageSize: number,
) {
  return useQuery({
    queryKey: [...assessmentQueryKeys.list(filters), page, pageSize],
    queryFn: () => getAdminAssessments(filters, page, pageSize),
    placeholderData: (previous) => previous,
  });
}

/**
 * Query hook for one assessment's summary.
 *
 * Not retried: the failure this screen expects is a 404 for an id that is not
 * an assessment, and retrying that only delays telling the reader so.
 */
export function useAdminAssessment(id: string) {
  return useQuery({
    queryKey: assessmentQueryKeys.detail(id),
    queryFn: () => getAdminAssessment(id),
    retry: false,
  });
}
